/**
 * Browser entry point.
 *
 * Provider order is the one F7 §6.1 fixes, and it matters:
 *   `ThemeProvider` (Vibe — puts the theme class on `<body>`, so every `--primary-color` style
 *   token resolves) → `UiProvider` (ours — the toast, dialog and panel hosts) → QueryClient →
 *   tRPC → Router.
 *
 * The CSS is imported here, once. `@elizmonday/ui/tokens.css` pulls in `@vibe/core/tokens`
 * itself and layers ours on top, which is what lets this app depend on `@elizmonday/ui` alone
 * and never on `@vibe/core` (U2). `fonts.css` follows it: the self-hosted Figtree/Poppins
 * subsets land in `apps/web/public/fonts/` with their OFL licences (B4, open question 102), so
 * the product finally renders in the reference faces and the pixel fixtures mean something.
 */
import '@elizmonday/ui/tokens.css';
import '@elizmonday/ui/fonts.css';
import '@elizmonday/ui/components.css';
import './styles.css';

import { QueryClientProvider } from '@tanstack/react-query';
import { ThemeProvider, UiProvider } from '@elizmonday/ui';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { RouterProvider } from 'react-router';
import { ErrorToastBridge } from './app/ErrorToastBridge';
import { installGlobalErrorHandlers } from './app/errors';
import { createQueryClient, createTrpcClient, TRPCProvider } from './lib/trpc';
import { createRouter } from './routes';
/**
 * M0-F9: every phone rule, in one file, imported **last** so it wins on cascade order over
 * every module stylesheet the router pulled in above. Nothing else in the app carries a
 * `max-width` media query (`apps/web/src/mobile.test.ts` enforces it).
 */
import './mobile.css';

/*
 * P-011: installed before anything can reject. A dropped promise — `void save()` — is reported
 * to the person and stopped from reaching `window` as an uncaught error, which is what the
 * round 6h sweep recorded as `page-error`.
 */
installGlobalErrorHandlers();

const container = document.getElementById('root');
if (!container) throw new Error('#root is missing from index.html');

const queryClient = createQueryClient();
const trpcClient = createTrpcClient();
const router = createRouter();

/**
 * Theme is per user (`[as §Per-user settings]`) and arrives with `shell.bootstrap`. Phase 1
 * only ever renders `light`: a stored `dark` or `black` is preserved but not applied (F7 E7),
 * so the OS preference is deliberately not read here.
 */
createRoot(container).render(
  <StrictMode>
    <ThemeProvider systemTheme="light">
      <UiProvider>
        <ErrorToastBridge />
        <QueryClientProvider client={queryClient}>
          <TRPCProvider queryClient={queryClient} trpcClient={trpcClient}>
            <RouterProvider router={router} />
          </TRPCProvider>
        </QueryClientProvider>
      </UiProvider>
    </ThemeProvider>
  </StrictMode>,
);
